/**
 * アルコール測定のみ（共有端末）。
 * 打刻と切り離して測定だけを行い、タイムスタンプ付きで記録する（測定連動の勤怠 alcohol_linked_attendance の会社のみ）。
 * 出勤予定の有効範囲（マスタの設定。既定は前後30分）の測定はサーバーがその勤怠へ自動で紐付ける。
 * 検知器の経路はホームの打刻時と同じ（開発者専用アプリ → WebUSB → PC経由）。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, startAlcoholCheck } from '../services/api';
import { companionPing, startCompanionCheck } from '../services/alcohol-companion';
import { isWebUSBSupported, startAlcoholUSB } from '../services/alcohol-usb';
import { rawToAlcoholMgL, isAlcoholSentinelRaw } from '../services/alcohol-value';
import { judgeAlcohol, getAlcoholThresholds } from '../utils/alcoholThresholds';
import { AppHeader } from '../components/AppHeader';
import type { AuthUser } from '../hooks/useAuth';
import { useAlcoholWorkflowSettings } from '../hooks/useAlcoholWorkflowSettings';

type Phase = 'idle' | 'warming' | 'waiting' | 'no_breath' | 'timeout' | 'manual' | 'saving' | 'done';
interface CheckRow {
  id: string; measured_at: string; alcohol_value: number | null; is_manual: number;
  work_session_id: string | null; vessel_name?: string | null; linked_work_type?: string | null; linked_started_at?: string | null;
}

const fmtHm = (iso: string) => new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
const fmtMd = (iso: string) => new Date(iso).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short' });
const fmtMgL = (v: number | null | undefined) => v == null ? '--' : `${Number(v).toFixed(3)} mg/L`;

const BreathIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>
);

export default function AlcoholCheckPage({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const { window_label: winLabel } = useAlcoholWorkflowSettings();
  const [phase, setPhase] = useState<Phase>('idle');
  const [countdown, setCountdown] = useState(10);
  const [retry, setRetry] = useState(0);
  const [errText, setErrText] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [result, setResult] = useState<{ value: number; manual: boolean; measured_at: string; linked: string | null } | null>(null);
  const [recent, setRecent] = useState<CheckRow[]>([]);
  const stopRef = useRef<(() => void) | null>(null);
  const maxRef = useRef(10);

  const loadRecent = useCallback(async () => {
    try {
      const r = await apiFetch<{ data: CheckRow[] }>('/alcohol-workflow/checks?days=7');
      setRecent(r?.data ?? []);
    } catch { /* 圏外なら前回表示のまま */ }
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const stopReading = () => { stopRef.current?.(); stopRef.current = null; };
  useEffect(() => () => stopReading(), []);

  const save = useCallback(async (value: number, manual: boolean) => {
    setPhase('saving');
    const measuredAt = new Date().toISOString();
    try {
      const vid = localStorage.getItem('catms_device_vessel_id') || localStorage.getItem('catms_last_vessel_id') || undefined;
      const r = await apiFetch<{ data: { linked_session_id: string | null; measured_at: string } }>('/alcohol-workflow/checks', {
        method: 'POST', body: JSON.stringify({ alcohol_value: value, alcohol_manual: manual, measured_at: measuredAt, vessel_id: vid }),
      });
      setResult({ value, manual, measured_at: r?.data?.measured_at ?? measuredAt, linked: r?.data?.linked_session_id ?? null });
      setPhase('done');
      loadRecent();
    } catch (e: any) {
      setErrText(e?.message ?? '保存に失敗しました');
      setPhase('timeout');
    }
  }, [loadRecent]);

  const handleRaw = useCallback((raw: number) => {
    stopReading();
    if (isAlcoholSentinelRaw(raw)) { setRetry(n => n + 1); setPhase('no_breath'); return; }
    void save(rawToAlcoholMgL(raw), false);
  }, [save]);

  const start = useCallback(async () => {
    stopReading();
    setErrText('');
    setPhase('warming'); setCountdown(10); maxRef.current = 10;
    const ping = await companionPing();
    if (ping.ok) {
      const p = startCompanionCheck(
        (c) => { if (c > maxRef.current) maxRef.current = c; setPhase('warming'); setCountdown(c); },
        () => setPhase('waiting'),
        (v) => { if (v === 0xAA || v === 0xEE) { setRetry(n => n + 1); setPhase('no_breath'); p.close(); return; } handleRaw(v); },
        () => setPhase(retry > 0 ? 'no_breath' : 'timeout'),
        (msg) => { setErrText(msg); setPhase('timeout'); },
        (hex) => { if (hex.includes('呼気未検知')) { setRetry(n => n + 1); setPhase('no_breath'); p.close(); } },
      );
      stopRef.current = () => p.close();
      return;
    }
    if (isWebUSBSupported()) {
      const cancel = await startAlcoholUSB((ev) => {
        if (ev.type === 'warming') { setPhase('warming'); setCountdown(ev.countdown); }
        if (ev.type === 'waiting') setPhase('waiting');
        if (ev.type === 'done') handleRaw(ev.value);
        if (ev.type === 'error' && ev.message !== 'cancelled') { setErrText(ev.message); setPhase('timeout'); }
      });
      stopRef.current = cancel;
      return;
    }
    try {
      const es = await startAlcoholCheck(
        (c) => { if (c > maxRef.current) maxRef.current = c; setPhase('warming'); setCountdown(c); },
        () => setPhase('waiting'),
        (v) => handleRaw(v),
        () => setPhase('timeout'),
        () => setPhase('timeout'),
        () => { setRetry(n => n + 1); setPhase('no_breath'); },
      );
      stopRef.current = () => es.close();
    } catch { setPhase('timeout'); }
  }, [handleRaw, retry]);

  const cancel = () => { stopReading(); setPhase('idle'); setRetry(0); setErrText(''); };

  const th = getAlcoholThresholds();
  const card: React.CSSProperties = { width: '100%', maxWidth: 360, background: '#fff', borderRadius: 20, border: '1px solid #e4e9f0', padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 };
  const primary: React.CSSProperties = { width: '100%', height: 72, borderRadius: 18, border: 'none', background: 'linear-gradient(145deg,#0057d9,#0369a1)', color: '#fff', fontSize: 18, fontWeight: 800, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,87,217,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 };
  const secondary: React.CSSProperties = { width: '100%', height: 52, borderRadius: 14, border: '1.5px solid #e4e9f0', background: '#fff', color: '#5f6b7c', fontSize: 15, fontWeight: 600, cursor: 'pointer' };
  const link: React.CSSProperties = { width: '100%', height: 52, borderRadius: 14, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 15, fontWeight: 700, cursor: 'pointer' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      <AppHeader title="アルコール測定のみ" />
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>

        {phase === 'idle' && (
          <div style={card}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(145deg,#0057d9,#0369a1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0,87,217,0.3)' }}><BreathIcon /></div>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>{user.user_name} さん</p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', lineHeight: 1.7 }}>
              打刻とは別に、アルコール測定だけを記録します。<br />
              <strong style={{ color: '#0f172a' }}>出勤予定の{winLabel}</strong>に測った記録は、その勤怠に自動で紐付きます。
            </p>
            <button onClick={start} style={primary}><BreathIcon />測定を始める</button>
            <button onClick={() => { setManualValue(''); setPhase('manual'); }} style={link}>検知器が使えないとき（手入力）</button>
          </div>
        )}

        {phase === 'warming' && (
          <div style={card}>
            <div style={{ width: 130, height: 130, borderRadius: '50%', background: 'linear-gradient(145deg,#6366f1,#4f46e5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 40, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{countdown}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>秒</span>
            </div>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>測定の準備中です</p>
            <p style={{ margin: 0, fontSize: 14, color: '#64748b', textAlign: 'center' }}>あと <strong style={{ color: '#4f46e5' }}>{countdown}</strong> 秒で測定できます</p>
            <button onClick={cancel} style={secondary}>キャンセル</button>
          </div>
        )}

        {phase === 'waiting' && (
          <div style={card}>
            <div style={{ width: 130, height: 130, borderRadius: '50%', background: 'linear-gradient(145deg,#3b82f6,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(59,130,246,0.4)' }}><BreathIcon /></div>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>息を吹きかけてください</p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>検知器のマウスピースに向かって<br />結果が出るまで4秒ほど強く吹き続けてください</p>
            <button onClick={cancel} style={secondary}>キャンセル</button>
          </div>
        )}

        {phase === 'no_breath' && (
          <div style={card}>
            <div style={{ width: 120, height: 120, borderRadius: '50%', background: 'linear-gradient(145deg,#0057d9,#0369a1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <BreathIcon />{retry > 0 && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}>{retry}回目</span>}
            </div>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>息が感知されませんでした</p>
            <p style={{ margin: 0, fontSize: 13, color: '#0369a1', textAlign: 'center', lineHeight: 1.6 }}>マウスピースに口をしっかり当て、<br />ゆっくりと深く息を吹き込んでください。</p>
            <button onClick={start} style={primary}>もう一度測定する</button>
            <button onClick={cancel} style={secondary}>キャンセル</button>
          </div>
        )}

        {phase === 'timeout' && (
          <div style={card}>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>デバイスが応答しませんでした</p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>{errText || '検知器が接続されているか確認してください'}</p>
            <button onClick={start} style={primary}>再試行</button>
            <button onClick={() => { setManualValue(''); setPhase('manual'); }} style={link}>アルコール値を手動入力する</button>
            <button onClick={cancel} style={secondary}>キャンセル</button>
          </div>
        )}

        {phase === 'manual' && (
          <div style={card}>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>アルコール値を入力</p>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center' }}>検知器の数値を mg/L で入力してください（手入力は陸上に通知されます）</p>
            <div style={{ display: 'flex', alignItems: 'center', border: '2px solid #6366f1', borderRadius: 12, overflow: 'hidden', background: '#fff', width: '100%', maxWidth: 240 }}>
              <input type="number" step="0.001" min="0" max="1" value={manualValue} onChange={e => setManualValue(e.target.value)} placeholder="例: 0.000"
                style={{ flex: 1, border: 'none', padding: '14px 16px', fontSize: 22, fontWeight: 700, outline: 'none', textAlign: 'center', color: '#0f172a', minWidth: 0 }} />
              <span style={{ padding: '0 14px', color: '#64748b', fontWeight: 600, fontSize: 13 }}>mg/L</span>
            </div>
            <button disabled={manualValue === '' || isNaN(Number(manualValue))} onClick={() => save(Number(manualValue), true)}
              style={{ ...primary, background: manualValue !== '' ? 'linear-gradient(145deg,#059669,#047857)' : '#e2e8f0', color: manualValue !== '' ? '#fff' : '#94a3b8', boxShadow: 'none' }}>
              この値で記録する
            </button>
            <button onClick={cancel} style={secondary}>← 戻る</button>
          </div>
        )}

        {phase === 'saving' && (
          <div style={card}><p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#64748b' }}>記録しています...</p></div>
        )}

        {phase === 'done' && result && (() => {
          const j = judgeAlcohol(result.value, th);
          const color = j === 'critical' ? '#dc2626' : j === 'warning' ? '#d97706' : '#059669';
          const label = j === 'critical' ? '法定基準値を超えています' : j === 'warning' ? 'アルコール検知（要上長確認）' : '正常';
          return (
            <div style={card}>
              <div style={{ width: 120, height: 120, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 0 12px ${color}22` }}>
                <span style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>{Number(result.value).toFixed(3)}</span>
              </div>
              <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color }}>{label}</p>
              <div style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
                <div>🕒 測定時刻　<strong style={{ fontSize: 15 }}>{fmtMd(result.measured_at)} {fmtHm(result.measured_at)}</strong></div>
                <div>📏 測定値　{fmtMgL(result.value)}{result.manual && <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '1px 6px' }}>手入力</span>}</div>
                <div>{result.linked ? '🔗 出勤予定の勤怠に紐付けました' : `📌 記録しました（出勤予定の${winLabel}に入れば勤怠に紐付きます）`}</div>
              </div>
              <button onClick={() => navigate('/home')} style={primary}>ホームに戻る</button>
              <button onClick={cancel} style={secondary}>続けて測定する</button>
            </div>
          );
        })()}

        {/* 直近の記録（タイムスタンプ） */}
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', margin: '4px 0 8px' }}>最近7日間の測定記録</div>
          {recent.length === 0 ? (
            <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: 16 }}>記録はありません</div>
          ) : recent.map(r => {
            const j = judgeAlcohol(r.alcohol_value ?? 0, th);
            const color = j === 'critical' ? '#dc2626' : j === 'warning' ? '#d97706' : '#059669';
            return (
              <div key={r.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{fmtMd(r.measured_at)} {fmtHm(r.measured_at)}
                    <span style={{ marginLeft: 8, fontWeight: 600, color }}>{fmtMgL(r.alcohol_value)}</span>
                    {r.is_manual === 1 && <span style={{ marginLeft: 6, fontSize: 10, color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '1px 6px' }}>手入力</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {r.work_session_id
                      ? `🔗 ${r.linked_started_at ? fmtHm(r.linked_started_at) : ''}〜 ${r.linked_work_type ?? '勤怠'} に紐付け済み`
                      : `未紐付け（勤怠の予定が${winLabel}に無い）`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
